<script setup lang="ts">
/**
 * Appoint Director (B32101) — SG 공통 프리셋 **스튜디오** 패널(문서 재현), **다건 선임**.
 *   Stage 1 Directors' Resolution ×1 (현직 이사 서명) · Stage 2 Form 45(공통 최신본) ×선임 이사 수.
 *   cdd(문서 재현 입력엔진)·서명자·DocScope 는 aura/agm 재사용(B32111 과 동일 구조).
 *
 * 저장(TASK_DATA.formData):
 *   · presetForm{values(f45_{id}/…), signers, local{newIds}} — 파이썬 BM03B E-Filing(AppointWithdrawOfficerEFiling,
 *     B32111 과 공용) 이 이걸로 선임 목록을 시드한다. taskTypeCd:'B32101' 로 선임 전용 화면이 된다.
 *   · 첫 선임 이사 평면 필드(buildAppointDirectorTaskFormData) — 상세보기(register/appointDirector.ts) 호환.
 *   · directors[{contactCd, fullName, appointmentDate}] — 레지스터 등록을 이사별로(registerToRegisterStore).
 */
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { fetchSessionClientNm } from '@/features/startTask/preset_task/sg/composables/fetchSessionClientNm';
import { getB04Options, loadCmBaseCodeOptions, nationalityLabelForOutput, resolveCmBaseCodeValue } from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';
import { provideAgmFill, bareKey, scopeOf } from '@/features/startTask/my_preset/sg/aura/agm/cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from '@/features/startTask/my_preset/sg/aura/agm/cdd/agmSignersContext';
import { getActiveDirectors } from '@/features/startTask/preset_task/sg/components/sgDocGenEntityApi';
import { buildSgPresetPrepareDocumentItem } from '@/features/startTask/preset_task/sg/components/presetDocCustSave';
import { buildAppointedOfficerSignerJson } from '@/features/startTask/preset_task/sg/components/appoint-director/composables/appointDirectorSigners';
import { directorIdLabel } from '@/features/startTask/preset_task/sg/components/appoint-director/composables/directorIdLabel';
import { buildContactDataFromSelectedPerson } from '@/features/startTask/preset_task/sg/components/appoint-director/composables/appointDirectorContact';
import { applyDirectorStructuredProfileFromContactData } from '@/features/startTask/preset_task/sg/components/appoint-director/composables/useFormPayload';
import {
  createEmptyAppointDirectorForm,
  buildAppointDirectorTaskFormData,
  syncAppointDirectorManualContactData,
  buildDirectorsSignersJson,
  type AppointDirectorFormModel,
} from '@/features/startTask/preset_task/sg/components';
import AppointDirectorPackageDoc from '@/features/startTask/preset_task/sg/components/appoint-director/studio/AppointDirectorPackageDoc.vue';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: Record<string, any> | null;
  editTaskId?: number | null;
  studio?: boolean;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'request-person-lookup', context: Record<string, any>): void;
  (e: 'request-address-lookup', context: Record<string, any>): void;
  (e: 'progress-change', pct: number): void;
}>();

const TASK_CODE = 'B32101';
const router = useRouter();
const sessionStore = useSessionStore();
const fill = provideAgmFill();
const signersCtx = provideAgmSigners();
const creating = ref(false);
const progress = ref('');
const taskConfigSteps = ref<string[]>([]);
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  emit('progress-change', Math.min(100, Math.round((fill.filledCount.value / total) * 100)));
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], emitProgress, { immediate: true });

const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const effectiveTaskTitle = computed(() => (props.taskTitle || '').trim());
const docRef = ref<InstanceType<typeof AppointDirectorPackageDoc> | null>(null);
const autoLocal = ref<{ newIds: number[] } | null>(null);
const lastAutofillCd = ref('');

const entityCd = computed(() => String(props.entityData?.icpCd || props.entityData?.entityCd || '').trim());
provide(AGM_ENTITY_CD, entityCd);

const st = (v: unknown) => String(v ?? '').trim();
let signerSeq = 0;
function mkSigner(name: string): SignerEntry {
  return { id: `ad-${signerSeq++}`, name: st(name), title: 'Director', role: 'director', isManual: true, showTitle: true, _signerLayout: 'box' } as SignerEntry;
}

/** Form 45 서명 블록 — 이름 아래 "Director" 직함(buildAppointedOfficerSignerJson 은 title 을 안 넣는다). */
function withDirectorTitle(json: string): string {
  if (!json) return json;
  try { return JSON.stringify((JSON.parse(json) as any[]).map((s) => ({ ...s, title: 'Director', showTitle: true }))); } catch { return json; }
}

/** 국가 calling code — COUNTRY_CD(B05xxx) 의 BIGO3(예 +65). Form 45 "Mobile phone number: +( )" 표시용. */
const dialCache = new Map<string, string>();
async function dialCodeOf(countryCd: string): Promise<string> {
  const cc = st(countryCd);
  if (!cc) return '';
  if (dialCache.has(cc)) return dialCache.get(cc)!;
  let bigo3 = '';
  try {
    const res: any = await baseCodeApi.getCodeByTotalCode('CM', cc);
    const code: any = res?.code || res?.data?.code || res?.data || {};
    bigo3 = st(code.bigo3 || code.BIGO3);
  } catch { /* ignore */ }
  if (bigo3 && !bigo3.startsWith('+')) bigo3 = `+${bigo3}`;
  dialCache.set(cc, bigo3);
  return bigo3;
}

// 회사 정보 + 비서(lodger) + DR 서명자(현직 이사 전원) 자동채움.
async function autofill(ent: Record<string, any> | null) {
  if (!ent) return;
  const cd = entityCd.value;
  if (cd && cd === lastAutofillCd.value) return;
  lastAutofillCd.value = cd;
  const set = (k: string, val: unknown) => { const s = st(val); if (s) fill.setValue(k, s); };
  set('company_name', ent.companyName || ent.entityName || ent.ENTITY_NAME);
  set('company_registration_no', ent.registrationNo || ent.regNo || ent.REG_NO);
  if (cd && !st(fill.values.company_registration_no)) {
    try {
      const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
      const src: any = res?.data?.data ?? res?.data ?? res ?? {};
      set('company_registration_no', src.registrationNo);
    } catch { /* ignore */ }
  }
  if (!st(fill.values.secretary_firm_name)) {
    try {
      const customer: any = sessionStore.getCustomerInfo?.();
      const nm = st(customer?.name) || (await fetchSessionClientNm(st(customer?.code)).catch(() => ''));
      if (nm) fill.setValue('secretary_firm_name', nm);
    } catch { /* ignore */ }
  }
  if (cd) {
    try {
      // ⚠ getActiveDirectors 는 배열이 아니라 { data: { directors } } — 예전엔 .map 에서 throw → catch 로 묻혀 화면 서명자가 비었다.
      const res: any = await getActiveDirectors(cd);
      const dirs: any[] = Array.isArray(res) ? res : (res?.data?.directors || []);
      const names: string[] = dirs.map((d: any) => st(d.name || d.contactName || d.fullName || d.officerName)).filter(Boolean);
      if (names.length && !signersCtx.signersOf('dr_Signers').length) signersCtx.setSigners('dr_Signers', names.map((n) => mkSigner(n)));
    } catch { /* ignore */ }
  }
}
watch(() => props.entityData, (ent) => { if (!editMode.value) void autofill(ent); }, { immediate: true, deep: true });

// ✨ 인물 lookup — 선임 이사 = 글로벌 CONTACT(appointDirectorFlow 'appointedDirector'), 증인 = CONTACT.
/** 조회를 요청한 칸 — 결과는 이 칸 기준으로 넣는다(조회 중 다른 칸 클릭에 영향받지 않게). */
let lookupKey = '';
fill.setActivateHandler((key: string) => {
  const bk = bareKey(key);
  if (bk === 'director_name') {
    lookupKey = key;
    emit('request-person-lookup', { fieldGroup: 'appointedDirector', scopedKey: key, defaultRole: 'CONTACT', lookupType: 'contact', subjectLabel: 'Appointed director (contact)' });
  } else if (bk === 'witness_name') {
    lookupKey = key;
    emit('request-person-lookup', { fieldGroup: 'witness', scopedKey: key, defaultRole: 'CONTACT', lookupType: 'contact', subjectLabel: 'Witness (contact)' });
  }
});

function nationalityLabelOf(raw: Record<string, any>): string {
  const label = st(raw.nationalityName || raw.nationality);
  if (label) return label;
  const cd = st(raw.NATIONALITY_CD || raw.nationalityCd);
  return cd ? nationalityLabelForOutput(cd) : '';
}

function applyPerson(person: Record<string, any>) {
  const key = lookupKey || fill.activeKey.value;
  if (!key) return;
  lookupKey = '';
  const sc = scopeOf(key);
  const set = (k: string, val: unknown) => { const s = st(val); if (s) fill.setValue(sc + k, s); };
  const fullName = person.name || person.contactName || person.fullName || `${person.firstName || ''} ${person.lastName || ''}`.trim();
  if (bareKey(key) === 'witness_name') {
    set('witness_name', fullName);
    set('witness_address', person.residentialAddress || person.address || person.primaryAddress);
    return;
  }
  // 선임 이사 — 문서 칸 + BM03B E-Filing 이 읽는 키(B32111 과 동일: director_contact_cd / _id_type / _country_cd / _area_code).
  const contactData = buildContactDataFromSelectedPerson(person as OfficerDetail);
  const tmp = createEmptyAppointDirectorForm();
  applyDirectorStructuredProfileFromContactData(tmp, contactData);
  set('director_name', fullName);
  set('director_identification_no', person.nricNo || person.passportNo || person.contactRegNo || person.identificationNo);
  set('director_nationality', nationalityLabelOf(person));
  set('director_date_of_birth', String(person.dateOfBirth || person.DATE_OF_BIRTH || person.dob || '').slice(0, 10));
  set('director_email', person.contactEmail || person.email || person.CONTACT_EMAIL);
  set('director_address', tmp.directorAddress || person.residentialAddress || person.address || person.primaryAddress);
  set('director_alternate_address', tmp.directorAlternateAddress || person.alternateAddress || person.contactAddress);
  set('director_mobile_no', person.mobileNo || person.MOBILE_NO || person.phone);
  set('director_id_type', person.idTypeName || person.idType || tmp.directorIdTypeCd);
  set('director_country_cd', tmp.directorMobileCountryCd || person.countryCd);
  set('director_area_code', tmp.directorMobileAreaCode || person.areaCode);
  set('director_contact_cd', person.contactCd || person.CONTACT_CD);
}
watch(() => props.selectedPerson, (p) => { if (p) applyPerson(p as Record<string, any>); });

// 주소 버튼(📍) → 챗봇 주소 입력(appointDirectorFlow 의 directorAddress / directorAlternateAddress).
fill.setAddressHandler((key: string) => {
  const bk = bareKey(key);
  const fg = bk === 'director_alternate_address' ? 'directorAlternateAddress' : 'directorAddress';
  const label = bk === 'director_alternate_address' ? 'Contact address' : bk === 'witness_address' ? 'Witness address' : 'Residential address';
  emit('request-address-lookup', { fieldGroup: fg, scopedKey: key, addresses: [], directInput: true, subjectLabel: label });
});
watch(() => props.selectedAddress, (sel) => {
  if (!sel?.value) return;
  const key = st(props.activeAddressContext?.scopedKey) || fill.activeKey.value;
  if (key) fill.setValue(key, sel.value);
});

onMounted(async () => {
  void loadCmBaseCodeOptions('B04').catch(() => undefined);
  try { const c = props.selection.task.TASK_CODE; if (c) { const cfg = await getTaskConfigByCode(c); taskConfigSteps.value = cfg?.processSteps || []; } } catch { /* ignore */ }
});

function gatherState(): Record<string, any> {
  return { v: 3, values: { ...fill.exportPayload() }, signers: signersCtx.exportAll(), local: docRef.value?.gatherLocalState?.() };
}

/** 이사 1인 값을 f45_1/ 사본으로 (예전 1인 저장분 → 다건 사본). 비어 있는 칸만. */
function seedFirstCopy(pairs: Array<[string, unknown]>) {
  for (const [k, v] of pairs) {
    const s = st(v);
    if (s && !st(fill.values[`f45_1/${k}`])) fill.setValue(`f45_1/${k}`, s);
  }
}
/**
 * Edit 복원 — 세 가지 저장 형식 모두:
 *   ① 다건 스튜디오(v3) / B32111 패널 저장분: values 에 f45_{id}/ 사본 → 그대로.
 *   ② 1인 스튜디오(v2, 2026-10-07 하루): values 최상위 director_* → f45_1/.
 *   ③ 예전 폼(AppointDirectorForm): 평면 camelCase → f45_1/.
 */
function restoreState(s: any): void {
  if (!s) return;
  let ids: number[] = [];
  if (s.values && typeof s.values === 'object') {
    // 빈 값 먼저, 값 있는 것 나중 — 공유 키(회사명·UEN) '' 전파로 회사명이 지워지는 것 방지.
    const entries = Object.entries(s.values).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)] as const);
    for (const [k, v] of entries) if (!v.trim()) fill.setValue(k, v);
    for (const [k, v] of entries) if (v.trim()) fill.setValue(k, v);
    ids = [...new Set(Object.keys(s.values).map((k) => Number(k.match(/^f45_(\d+)\//)?.[1] || 0)).filter((n) => n > 0))].sort((a, b) => a - b);
    if (!ids.length && st(s.values.director_name)) {
      const v = s.values as Record<string, unknown>;
      seedFirstCopy([
        ['director_name', v.director_name], ['director_identification_no', v.director_identification_no],
        ['director_nationality', v.director_nationality], ['director_address', v.director_address],
        ['director_alternate_address', v.director_alternate_address], ['director_date_of_birth', v.director_date_of_birth],
        ['director_email', v.director_email], ['director_mobile_no', v.director_contact_no || v.director_mobile_no],
        ['director_contact_cd', v.director_contact_cd], ['director_id_type', v.director_id_type_cd],
        ['director_country_cd', v.director_mobile_country_cd], ['director_area_code', v.director_mobile_area_code],
        ['appointment_effective_date', v.appointment_effective_date], ['execution_date', v.execution_date],
        ['witness_name', v.witness_name], ['witness_address', v.witness_address],
      ]);
      ids = [1];
    }
    if (s.signers) signersCtx.importAll(s.signers);
  } else {
    const LEGACY: Array<[string, string]> = [
      ['companyName', 'company_name'], ['companyRegistrationNo', 'company_registration_no'],
      ['secretaryFirmName', 'secretary_firm_name'], ['resolutionDate', 'resolution_date'],
    ];
    for (const [from, to] of LEGACY) if (st(s[from])) fill.setValue(to, st(s[from]));
    if (st(s.directorName)) {
      seedFirstCopy([
        ['director_name', s.directorName], ['director_identification_no', s.directorIdentificationNo],
        ['director_nationality', nationalityLabelForOutput(st(s.directorNationality)) || s.directorNationality],
        ['director_address', s.directorAddress], ['director_alternate_address', s.directorAlternateAddress],
        ['director_date_of_birth', s.directorDateOfBirth], ['director_email', s.directorEmail],
        ['director_mobile_no', s.directorContactNo], ['director_contact_cd', s.contactCd],
        ['director_id_type', s.directorIdTypeCd], ['director_country_cd', s.directorMobileCountryCd],
        ['director_area_code', s.directorMobileAreaCode], ['appointment_effective_date', s.appointmentEffectiveDate],
        ['execution_date', s.executionDate],
      ]);
      ids = [1];
    }
  }
  const local = s.local && Array.isArray(s.local.newIds) && s.local.newIds.length ? s.local : (ids.length ? { newIds: ids } : null);
  if (local) { autoLocal.value = { newIds: local.newIds }; docRef.value?.restoreLocalState?.(local); }
}
defineExpose({ gatherState, restoreState, createTask: handleCreateTask });

// 사본(f45_{id}/)에서 최상위로 상속하면 안 되는 키(회사 키 제외).
const OWNED = [
  'director_name', 'director_identification_no', 'director_nationality', 'director_id_type',
  'director_email', 'director_mobile_no', 'director_country_cd', 'director_area_code', 'director_contact_cd',
  'director_address', 'director_alternate_address', 'director_date_of_birth', 'appointment_effective_date',
  'execution_date', 'witness_name', 'witness_address',
];
function scopedFormData(scope: string): Record<string, any> {
  const base = fill.exportPayload();
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(base)) { if (k.includes('/')) continue; if (OWNED.includes(bareKey(k))) continue; out[k] = v; }
  for (const [k, v] of Object.entries(base)) {
    if (!k.startsWith(scope)) continue;
    const bk = bareKey(k); const sv = st(v);
    if (sv === '' && bk in out && st(out[bk]) !== '') continue;
    out[bk] = v;
  }
  return out;
}

/** f45_{id}/ 사본 → 예전 폼 모델 (상세보기·레지스터 호환용 평면 필드 빌더 재사용). */
function toModel(id: number): AppointDirectorFormModel {
  const g = (k: string) => st(fill.values[`f45_${id}/${k}`]);
  const m = createEmptyAppointDirectorForm();
  m.companyName = st(fill.values.company_name);
  m.companyRegistrationNo = st(fill.values.company_registration_no);
  m.appointmentEffectiveDate = g('appointment_effective_date');
  m.directorName = g('director_name');
  m.directorAddress = g('director_address');
  m.directorAlternateAddress = g('director_alternate_address');
  m.directorIdentificationNo = g('director_identification_no');
  m.directorNationality = resolveCmBaseCodeValue(g('director_nationality'), getB04Options()) || g('director_nationality');
  m.directorDateOfBirth = g('director_date_of_birth');
  m.directorEmail = g('director_email');
  m.directorContactNo = g('director_mobile_no');
  m.directorMobileCountryCd = g('director_country_cd');
  m.directorMobileAreaCode = g('director_area_code');
  m.secretaryFirmName = st(fill.values.secretary_firm_name);
  m.resolutionDate = st(fill.values.resolution_date);
  m.executionDate = g('execution_date');
  m.contactCd = g('director_contact_cd');
  m.positionCd = 'B30027';
  return m;
}

async function handleCreateTask() {
  const title = effectiveTaskTitle.value;
  if (!title) { progress.value = 'Error: enter a Task Title in the chat first.'; return; }
  if (!props.entityData) { progress.value = 'Error: select an entity in the chat first.'; return; }
  const local = docRef.value?.gatherLocalState?.() || { newIds: [] };
  const v = fill.values;
  const news: number[] = (local.newIds || []).filter((id: number) => st(v[`f45_${id}/director_name`]) || st(v[`f45_${id}/director_identification_no`]));
  // 필수 칸 — 회사 · DR 날짜 · 선임 이사별 이름·ID·선임일(e-Filing entryDate / 레지스터 선임일)
  const missing: string[] = [];
  if (!st(v.company_name)) missing.push('Company name');
  if (!st(v.company_registration_no)) missing.push('Company No.');
  if (!st(v.resolution_date)) missing.push('Resolution date');
  if (!news.length) missing.push('at least one appointed director (Form 45)');
  news.forEach((id: number, i: number) => {
    const g = (k: string) => st(v[`f45_${id}/${k}`]);
    const lack = [['director_name', 'name'], ['director_identification_no', 'ID'], ['appointment_effective_date', 'effective date']]
      .filter(([k]) => !g(k)).map(([, l]) => l);
    if (lack.length) missing.push(`Director ${i + 1}: ${lack.join(', ')}`);
  });
  if (missing.length) { progress.value = `Please complete: ${missing.join(' · ')}`; return; }
  const taskTypeCd = props.selection.task.TASK_CODE || TASK_CODE;

  creating.value = true;
  progress.value = editMode.value ? 'Updating task...' : 'Creating task...';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing') || steps.includes('efiling') || ['B32101', 'B32102', 'B32103', 'B32104'].includes(String(taskTypeCd).trim()),
    };
    const firstModel = toModel(news[0]);
    syncAppointDirectorManualContactData(firstModel);
    const newDirectors = news.map((id) => ({
      director_name: st(v[`f45_${id}/director_name`]),
      director_identification_no: st(v[`f45_${id}/director_identification_no`]),
      director_id_label: directorIdLabel(v[`f45_${id}/director_id_type`], v[`f45_${id}/director_identification_no`]),
    }));
    // 레지스터 이사별 등록 — contactCd 가 모두 있을 때만(없는 행이 있으면 레지스터가 막는다 → 첫 이사 평면 경로로 폴백).
    const dirRows = news.map((id) => ({ contactCd: st(v[`f45_${id}/director_contact_cd`]), fullName: st(v[`f45_${id}/director_name`]), appointmentDate: st(v[`f45_${id}/appointment_effective_date`]) }));
    const companyBase = { company_name: st(v.company_name), company_registration_no: st(v.company_registration_no), secretary_firm_name: st(v.secretary_firm_name), resolution_date: st(v.resolution_date) };
    const taskFormData: Record<string, any> = {
      ...buildAppointDirectorTaskFormData(firstModel, entityCd.value),
      ...companyBase,
      taskTypeCd: TASK_CODE,
      new_directors: newDirectors,
      ...(dirRows.every((r) => r.contactCd) ? { directors: dirRows } : {}),
      presetForm: gatherState(),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ taskData: { formData: taskFormData } }) });
      try { await fetch(`/api/startTask/tasks/${taskId}/title`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ taskTitle: props.taskTitle }) }); } catch { /* optional */ }
    } else {
      const taskRes = await fetch('/api/startTask/tasks', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ entityCd: entityCd.value || null, taskTypeCd, taskTypeName: props.selection.task.TASK_NAME, taskTitle: title, taskFlow, officerData: taskFormData }) });
      const taskJson = await taskRes.json();
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) { progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`; creating.value = false; return; }
    }

    progress.value = 'Saving documents...';
    const drSigners = signersCtx.signersOf('dr_Signers');
    const drSignersJson = drSigners.length ? JSON.stringify(drSigners) : await buildDirectorsSignersJson(entityCd.value || '');
    const docsToSave: Array<Record<string, any>> = [];
    for (const d of (props.selection.documents || [])) {
      const tid = Number(d?.tmpId || 0); if (!tid) continue;
      const name = String(d.templateName || d.docCode || '');
      if (/Form\s*45(?!\s*B)/i.test(name)) {
        for (const id of news) {
          const fd = scopedFormData(`f45_${id}/`);
          // Form 45 "Mobile phone number: +( )" — 국가번호 + 지역 + 번호 (문서 표시만, e-Filing 은 원본 키 사용)
          const dial = await dialCodeOf(st(fd.director_country_cd));
          fd.director_mobile_no = [dial, st(fd.director_area_code), st(fd.director_mobile_no)].filter(Boolean).join(' ');
          fd.director_nationality = nationalityLabelForOutput(st(fd.director_nationality)) || st(fd.director_nationality);
          const pn = st(fd.director_name);
          const sj = withDirectorTitle(buildAppointedOfficerSignerJson({ name: pn, email: st(fd.director_email), role: 'director', contactCd: st(fd.director_contact_cd), contactRegNo: st(fd.director_identification_no) }));
          fd.signers = sj; fd.Signers = sj;
          const label = news.length > 1 ? `${name} — ${pn || `Director ${id}`}` : name;
          docsToSave.push(buildSgPresetPrepareDocumentItem({ templateId: tid, templateName: label, personName: pn, commonFormData: fd, entityCd: entityCd.value || undefined }));
        }
      } else {
        // DR (Appoint Director) — 현직 이사 전원 서명
        const drFd = { ...companyBase, new_directors: newDirectors, signers: drSignersJson, Signers: drSignersJson };
        docsToSave.push(buildSgPresetPrepareDocumentItem({ templateId: tid, templateName: name, personName: companyBase.company_name || 'Company', commonFormData: drFd, entityCd: entityCd.value || undefined }));
      }
    }
    if (docsToSave.length) {
      try {
        const r = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((x) => x.json());
        if (!r?.success) progress.value = `Documents save warning: ${r?.message || 'unknown'}`;
      } catch (err) { console.warn('[PreSetAppointDirector] prepare-documents failed:', err); }
    }
    try { await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include' }); } catch { /* ignore */ }
    try { await fetch('/api/startTask/preset/use-log', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }) }); } catch { /* ignore */ }
    emit('task-created', { taskId, taskTitle: title });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: unknown) {
    progress.value = `Error: ${err instanceof Error ? err.message : String(err)}`;
  } finally { creating.value = false; }
}
</script>

<template>
  <div class="ad-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Appoint Director' }}</div>
        <div class="co-subtitle">Director&rsquo;s resolution — Appoint director(s) (Singapore)</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>
    <div class="co-doc"><AppointDirectorPackageDoc ref="docRef" :auto-local="autoLocal" /></div>
    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!entityData || !effectiveTaskTitle || creating" @click="handleCreateTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot co-msg"><span class="co-err">{{ progress }}</span></div>
  </div>
</template>

<style scoped>
.ad-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.ad-panel.studio .co-msg { padding: 8px 12px; border: 1px solid #f3c9c4; border-radius: 8px; background: #fdf3f2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
